import {FC, useCallback, useEffect, useState} from 'react'
import {getPostPacing, setSpreadBulk, setSpreadForAccount} from './core/_requests'
import {BulkScope, PacingResponse} from './core/_models'

// "Burn rate" console: which profiles have already used up most of today's
// scheduled slots by the current hour. A profile at 80%+ before the afternoon
// dumped the whole day into one morning burst.
//
// The remedy is the existing per-account flag Account.xpost_spread_across_day —
// with it on, the bot only bundles cross-post copies whose hour is within 3
// hours of now (and never looks more than 3 hours back), so publishing stays in
// a ±3h window around the clock instead of front-loading.

const pctClass = (pct: number) => {
  if (pct >= 95) return 'text-danger'
  if (pct >= 80) return 'text-warning'
  if (pct >= 50) return 'text-gray-800'
  return 'text-success'
}

const barClass = (pct: number) => {
  if (pct >= 95) return 'bg-danger'
  if (pct >= 80) return 'bg-warning'
  if (pct >= 50) return 'bg-primary'
  return 'bg-success'
}

const PostPacingPage: FC = () => {
  const [data, setData] = useState<PacingResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [threshold, setThreshold] = useState(80)
  const [showAll, setShowAll] = useState(false)
  const [q, setQ] = useState('')
  const [qInput, setQInput] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({})
  const [flash, setFlash] = useState<string>('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getPostPacing({threshold, all: showAll, q})
      setData(res.data)
    } catch (e) {
      console.error('Failed to load post pacing:', e)
    }
    setLoading(false)
  }, [threshold, showAll, q])

  useEffect(() => {
    load()
  }, [load])

  const s = data?.summary

  const runBulk = async (enabled: boolean, scope: BulkScope) => {
    const label =
      scope === 'all'
        ? 'כל החשבונות במערכת'
        : scope === 'active'
        ? 'כל החשבונות הפעילים (מנוי בתוקף, לא נעולים)'
        : scope === 'pct'
        ? `${s?.accounts_at_100 || 0} החשבונות שיש להם פרופיל שסיים 100% מהיום`
        : `${s?.accounts_over_threshold || 0} החשבונות שמופיעים ברשימה`
    const verb = enabled ? 'להדליק' : 'לכבות'
    if (!window.confirm(`${verb} פרסום בחלון של ±3 שעות עבור ${label}?`)) return

    setBulkBusy(true)
    setFlash('')
    try {
      const ids =
        scope === 'listed'
          ? Array.from(new Set((data?.rows || []).map((r) => r.account_id).filter(Boolean) as string[]))
          : undefined
      const res = await setSpreadBulk(enabled, scope, {accountIds: ids, minPct: scope === 'pct' ? 100 : undefined})
      setFlash(
        `בוצע: ${res.data.modified.toLocaleString()} חשבונות עודכנו מתוך ${res.data.targeted.toLocaleString()} ` +
          `— סה״כ דולקים כעת: ${res.data.spread_on.toLocaleString()}`
      )
      await load()
    } catch (e: any) {
      setFlash(`שגיאה: ${e?.response?.data?.error || e.message}`)
    }
    setBulkBusy(false)
  }

  const toggleRow = async (accountId: string, enabled: boolean) => {
    setRowBusy((b) => ({...b, [accountId]: true}))
    try {
      await setSpreadForAccount(accountId, enabled)
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((r) => (r.account_id === accountId ? {...r, spread_on: enabled} : r)),
            }
          : d
      )
    } catch (e) {
      console.error('toggle failed', e)
    }
    setRowBusy((b) => ({...b, [accountId]: false}))
  }

  const maxHour = Math.max(1, ...(data?.hourly || []).map((h) => h.scheduled))

  return (
    <div className='container-fluid py-6' dir='rtl'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <h1 className='fw-bolder m-0'>קצב פרסום יומי</h1>
        <button className='btn btn-sm btn-light-primary' onClick={load} disabled={loading}>
          <i className='bi bi-arrow-clockwise me-1'></i>
          רענן
        </button>
      </div>
      <div className='text-gray-500 fs-7 mb-6'>
        כמה מהפוסטים המתוזמנים להיום כל פרופיל כבר פרסם עד עכשיו. פרופיל שעבר {threshold}% מוקדם ביום
        "שרף" את כל היום בבת אחת — הקהל של אחר הצהריים והערב לא רואה כלום.
        {data && (
          <span className='ms-2'>
            (שעון ישראל: <b>{data.israel_time}</b>, תאריך תזמון: <b>{data.date}</b>)
          </span>
        )}
      </div>

      {/* Summary cards */}
      {s && (
        <div className='row g-4 mb-6'>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>פרופילים מעל {data?.threshold}%</div>
                <div className='fs-2 fw-bolder text-danger'>{s.over_threshold.toLocaleString()}</div>
                <div className='fs-8 text-gray-500'>
                  {s.over_threshold_pct}% מתוך {s.profiles_with_slots.toLocaleString()} פרופילים עם תזמון היום
                </div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>חשבונות מושפעים</div>
                <div className='fs-2 fw-bolder'>{s.accounts_over_threshold.toLocaleString()}</div>
                <div className='fs-8 text-gray-500'>מתוך {s.accounts_active.toLocaleString()} חשבונות פעילים</div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>ניצול כלל־מערכתי</div>
                <div className='fs-2 fw-bolder'>{s.total_pct}%</div>
                <div className='fs-8 text-gray-500'>
                  {s.total_posted.toLocaleString()} / {s.total_slots.toLocaleString()} סלוטים
                </div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>חלון ±3 שעות דולק</div>
                <div className='fs-2 fw-bolder text-success'>{s.spread_on.toLocaleString()}</div>
                <div className='fs-8 text-gray-500'>
                  {s.spread_on_active.toLocaleString()} מהם פעילים · {s.accounts_total.toLocaleString()} חשבונות בסה״כ
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Distribution */}
      {s && (
        <div className='card card-flush mb-6'>
          <div className='card-header pt-4 pb-0'>
            <h3 className='card-title fs-5 fw-bold m-0'>פיזור פרופילים לפי אחוז ניצול</h3>
          </div>
          <div className='card-body py-4'>
            <div className='d-flex flex-wrap gap-6'>
              {[
                {label: '100% (סיימו את היום)', v: s.buckets.done_100, cls: 'text-danger'},
                {label: '80-99%', v: s.buckets.b80_99, cls: 'text-warning'},
                {label: '50-79%', v: s.buckets.b50_79, cls: 'text-gray-800'},
                {label: '20-49%', v: s.buckets.b20_49, cls: 'text-success'},
                {label: 'מתחת ל-20%', v: s.buckets.under_20, cls: 'text-gray-500'},
              ].map((b) => (
                <div key={b.label}>
                  <div className={`fs-3 fw-bolder ${b.cls}`}>{b.v.toLocaleString()}</div>
                  <div className='fs-8 text-gray-500'>{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hourly histogram — makes the front-loading visible */}
      {data && data.hourly.length > 0 && (
        <div className='card card-flush mb-6'>
          <div className='card-header pt-4 pb-0'>
            <h3 className='card-title fs-5 fw-bold m-0'>סלוטים לפי שעה (כהה = כבר פורסם)</h3>
          </div>
          <div className='card-body py-4'>
            <div className='d-flex align-items-end gap-2' style={{height: 140}}>
              {data.hourly.map((h) => {
                const totalH = (h.scheduled / maxHour) * 110
                const postedH = (h.posted / maxHour) * 110
                const isNow = h.hour === data.israel_hour
                return (
                  <div key={h.hour} className='d-flex flex-column align-items-center' style={{flex: 1}}>
                    <div
                      className='w-100 position-relative bg-light-primary rounded-top'
                      style={{height: Math.max(2, totalH)}}
                      title={`${h.hour}:00 — ${h.posted}/${h.scheduled} פורסמו`}
                    >
                      <div
                        className='w-100 position-absolute bottom-0 bg-primary rounded-top'
                        style={{height: Math.max(0, postedH)}}
                      ></div>
                    </div>
                    <div className={`fs-9 mt-1 ${isNow ? 'fw-bolder text-danger' : 'text-gray-500'}`}>{h.hour}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bulk control */}
      <div className='card card-flush mb-6 border border-primary border-dashed'>
        <div className='card-header pt-4 pb-0'>
          <h3 className='card-title fs-5 fw-bold m-0'>
            <i className='bi bi-clock-history me-2'></i>
            פרסום בחלון של ±3 שעות (הפעלה גורפת)
          </h3>
        </div>
        <div className='card-body py-4'>
          <div className='text-gray-600 fs-7 mb-4'>
            כשההגדרה דולקת, הבוט מצרף לפרסום רק עותקים שהשעה שלהם בטווח 3 שעות קדימה מהשעה הנוכחית
            (והוא ממילא מסתכל לכל היותר 3 שעות אחורה). התוצאה: הפרסום מתפרס על בוקר, צהריים וערב במקום
            להישרף בבת אחת בבוקר. ההגדרה היא <code>xpost_spread_across_day</code> ברמת חשבון — אותה הגדרה
            שקיימת בעמוד החשבון.
          </div>
          {flash && <div className='alert alert-info py-3 fs-7'>{flash}</div>}
          <div className='d-flex flex-wrap gap-3'>
            <button
              className='btn btn-sm btn-danger'
              disabled={bulkBusy || !s?.accounts_at_100}
              onClick={() => runBulk(true, 'pct')}
              title='רק חשבונות שיש להם פרופיל שכבר סיים 100% מהתזמון של היום'
            >
              הדלק רק ל-100% ({s?.accounts_at_100?.toLocaleString() || 0} חשבונות ·{' '}
              {s?.buckets?.done_100?.toLocaleString() || 0} פרופילים)
            </button>
            <button
              className='btn btn-sm btn-primary'
              disabled={bulkBusy}
              onClick={() => runBulk(true, 'active')}
            >
              הדלק לכל החשבונות הפעילים ({s?.accounts_active?.toLocaleString() || 0})
            </button>
            <button
              className='btn btn-sm btn-light-primary'
              disabled={bulkBusy}
              onClick={() => runBulk(true, 'all')}
            >
              הדלק לכל החשבונות ({s?.accounts_total?.toLocaleString() || 0})
            </button>
            <button
              className='btn btn-sm btn-light-warning'
              disabled={bulkBusy || !data?.rows?.length}
              onClick={() => runBulk(true, 'listed')}
            >
              הדלק רק לחשבונות שברשימה למטה ({s?.accounts_over_threshold?.toLocaleString() || 0})
            </button>
            <button
              className='btn btn-sm btn-light-danger ms-auto'
              disabled={bulkBusy}
              onClick={() => runBulk(false, 'all')}
            >
              כבה לכולם
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className='card card-flush mb-4'>
        <div className='card-body py-4'>
          <div className='row g-3 align-items-end'>
            <div className='col-md-3'>
              <label className='form-label fs-8 text-gray-500'>סף ניצול</label>
              <select
                className='form-select form-select-sm'
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
              >
                {[50, 60, 70, 80, 90, 95, 100].map((t) => (
                  <option key={t} value={t}>
                    {t}% ומעלה
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-4'>
              <label className='form-label fs-8 text-gray-500'>חיפוש (פרופיל / חשבון)</label>
              <input
                className='form-control form-control-sm'
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setQ(qInput)
                }}
                placeholder='חפש...'
              />
            </div>
            <div className='col-md-2'>
              <button className='btn btn-sm btn-primary w-100' onClick={() => setQ(qInput)}>
                חפש
              </button>
            </div>
            <div className='col-md-3'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='pacing-show-all'
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />
                <label className='form-check-label fs-7' htmlFor='pacing-show-all'>
                  הצג את כל הפרופילים (לא רק מעל הסף)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='card card-flush'>
        <div className='card-body py-4'>
          {loading ? (
            <div className='text-center py-10 text-gray-500'>טוען…</div>
          ) : !data?.rows?.length ? (
            <div className='text-center py-10 text-gray-500'>אין פרופילים תואמים</div>
          ) : (
            <>
              <div className='text-gray-500 fs-8 mb-3'>
                מוצגים {data.rows.length.toLocaleString()} מתוך {data.total_rows.toLocaleString()}
                {data.truncated && ' (רשימה נחתכה)'}
              </div>
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle fs-7 gy-3'>
                  <thead>
                    <tr className='fw-bold text-gray-500 fs-8 text-uppercase'>
                      <th>פרופיל</th>
                      <th>חשבון</th>
                      <th className='text-center'>פורסם / מתוזמן</th>
                      <th style={{minWidth: 160}}>ניצול</th>
                      <th className='text-center'>נותר</th>
                      <th className='text-center'>שגיאות</th>
                      <th className='text-center'>שעה אחרונה</th>
                      <th className='text-center'>חלון ±3ש׳</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.profile_id}>
                        <td>
                          <div className='fw-bold'>{r.name}</div>
                          <div className='fs-9 text-gray-500'>
                            {r.facebook_id}
                            {r.posts_per_day ? ` · מכסה ${r.posts_per_day}/יום` : ''}
                          </div>
                        </td>
                        <td>
                          {r.account_id ? (
                            <a
                              href={`/admin/accounts/${r.account_id}`}
                              target='_blank'
                              rel='noreferrer'
                              className='text-hover-primary'
                            >
                              {r.account_name || r.account_id}
                            </a>
                          ) : (
                            <span className='text-gray-400'>—</span>
                          )}
                          {!r.account_active && <span className='badge badge-light-secondary ms-2 fs-9'>לא פעיל</span>}
                        </td>
                        <td className='text-center fw-bold'>
                          {r.posted} / {r.total}
                        </td>
                        <td>
                          <div className='d-flex align-items-center gap-2'>
                            <div className='progress h-6px flex-grow-1' style={{minWidth: 70}}>
                              <div
                                className={`progress-bar ${barClass(r.pct)}`}
                                style={{width: `${Math.min(100, r.pct)}%`}}
                              ></div>
                            </div>
                            <span className={`fw-bolder ${pctClass(r.pct)}`} style={{minWidth: 46}}>
                              {r.pct}%
                            </span>
                          </div>
                        </td>
                        <td className='text-center'>{r.remaining}</td>
                        <td className='text-center'>
                          {r.errored ? <span className='text-danger'>{r.errored}</span> : '—'}
                        </td>
                        <td className='text-center text-gray-600'>{r.last_hour}:00</td>
                        <td className='text-center'>
                          {r.account_id ? (
                            <div className='form-check form-switch d-inline-block'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={r.spread_on}
                                disabled={!!rowBusy[r.account_id]}
                                onChange={(e) => toggleRow(r.account_id as string, e.target.checked)}
                              />
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PostPacingPage
